import {ITalentFinderEntry} from "api/types";
import ProfileImage from "components/ProfileImage";
import {certificationImages} from "constants/certifications";
import {MapPin, Clock, Monitor, Star, Zap, User} from "lucide-react";
import {ACCOUNTANT_BIO_TRUNCATE_LENGTH} from "pages/accountant/Profile/constants";
import {FC, useState} from "react";
import {useNavigate} from "react-router";

interface Props {
  talent: ITalentFinderEntry;
}

function levelColor(level: "low" | "average" | "high") {
  return level === "high" ? "#57B98F" : level === "average" ? "#F5A623" : "#F57A47";
}

// Overview bar color mirrors the profile status logic
function overviewColor(
  knowledge: "low" | "average" | "high",
  activity: "low" | "average" | "high",
  speed: "low" | "average" | "high",
) {
  if (activity === "low") return "#F57A47";
  if (activity === "average") return "#F5A623";
  if (knowledge === "high" && speed === "high") return "#57B98F";
  if (knowledge === "high") return "#57B98F";
  if (speed === "high") return "#57B98F";
  if (speed === "average" && knowledge === "average") return "#57B98F";
  if (speed === "low" && knowledge === "low") return "#F57A47";
  return "#F5A623";
}

// Hash-based color for cert tiles (same palette as Profile)
const certTileColors = ["#57B98F", "#F57A47", "#3D52AD", "#52A882"] as const;
function colorForName(name: string) {
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = (hash * 31 + name.charCodeAt(i)) | 0;
  return certTileColors[Math.abs(hash) % certTileColors.length];
}
function shortLabel(name: string) {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length >= 2) return (words[0][0] + words[1][0]).toUpperCase();
  return name.slice(0, 3).toUpperCase();
}

function initials(name: string) {
  return name
    .split(" ")
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? "")
    .join("");
}

const badgeConfig = {
  high: {
    label: "Top Talent",
    icon: <Star className="w-3 h-3" />,
    className: "text-brand-orange",
    style: {backgroundColor: "#FDF0E8"},
  },
  average: {
    label: "Rising Talent",
    icon: <Zap className="w-3 h-3" />,
    className: "text-[#3D52AD]",
    style: {backgroundColor: "#E8EAF6"},
  },
  low: {
    label: "New Graduate",
    icon: <User className="w-3 h-3" />,
    className: "text-brand-muted",
    style: {backgroundColor: "#EDE9E3"},
  },
} as const;

const Talent: FC<Props> = ({talent}) => {
  const navigate = useNavigate();
  const [bioExpanded, setBioExpanded] = useState(false);

  const goTo = talent.preview
    ? undefined
    : () => navigate(`/accountant/${talent.id}`, {state: {backLabel: "Back to Talents"}});

  const knowledge: "low" | "average" | "high" =
    talent.skillScore > 80 ? "high" : talent.skillScore > 20 ? "average" : "low";

  const {activity, speed, top} = talent;
  const badge = badgeConfig[top];

  const [_city, _state] = talent.location?.split(", ") || [];
  const city = _city?.toLowerCase() || "";
  const state = _state?.toUpperCase() || "";
  const locationStr = [city, state].filter(Boolean).join(", ");

  const displayName = talent.preview ? "Johnny Doeey" : talent.name;

  // employment: "Full Time" → "Available Full Time"
  const availabilityStr = talent.employment ? `Available ${talent.employment}` : null;

  // software & bio available only on full ITalent (not preview)
  const softwareExperience = !talent.preview ? talent.softwareExperience : undefined;
  const bio = !talent.preview ? talent.bio : undefined;

  const barColors = [
    overviewColor(knowledge, activity, speed),
    levelColor(knowledge),
    levelColor(activity),
    levelColor(speed),
  ];

  return (
    <div
      className={
        ["bg-white rounded-2xl overflow-hidden transition-shadow", !talent.preview && "cursor-pointer hover:shadow-md"]
          .asClass
      }
      style={{border: "1px solid #E8E4DF", boxShadow: "rgba(51,41,28,0.05) 0px 2px 12px"}}
      onClick={goTo}
    >
      {/* 4-segment metric bar — colors match profile level scheme */}
      <div className="flex h-1.5">
        {barColors.map((color, i) => (
          <div key={i} className="flex-1" style={{background: color}} />
        ))}
      </div>

      {/* Card body */}
      <div className={["px-5 py-4 flex items-start gap-4", talent.preview && "blur-sm select-none"].asClass}>
        {/* Avatar */}
        <div className="flex-shrink-0">
          {talent.preview ? (
            <div
              className="w-14 h-14 rounded-full flex items-center justify-center text-lg font-bold"
              style={{backgroundColor: "#E5E1DC", color: "#282D4D"}}
            >
              JD
            </div>
          ) : talent.image ? (
            <ProfileImage src={talent.image} size={56} />
          ) : (
            <div
              className="w-14 h-14 rounded-full flex items-center justify-center text-lg font-bold"
              style={{backgroundColor: "#E5E1DC", color: "#282D4D"}}
            >
              {initials(talent.name)}
            </div>
          )}
        </div>

        {/* Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            {/* Name + meta */}
            <div className="min-w-0">
              <h3 className="text-base font-bold" style={{color: "#2A1F14"}}>
                {displayName}
              </h3>
              <div className="flex flex-wrap items-center gap-3 mt-1">
                {locationStr && (
                  <span className="flex items-center gap-1 text-xs text-brand-muted">
                    <MapPin className="w-3.5 h-3.5" />
                    <span className="capitalize">{locationStr}</span>
                  </span>
                )}
                {availabilityStr && (
                  <span className="flex items-center gap-1 text-xs text-brand-muted">
                    <Clock className="w-3.5 h-3.5" />
                    {availabilityStr}
                  </span>
                )}
                {softwareExperience && softwareExperience.length > 0 && (
                  <span className="flex items-center gap-1 text-xs text-brand-muted">
                    <Monitor className="w-3.5 h-3.5" />
                    {softwareExperience.slice(0, 2).join(", ")}
                    {softwareExperience.length > 2 && ` +${softwareExperience.length - 2}`}
                  </span>
                )}
              </div>
            </div>

            {/* Badge + view profile */}
            <div className="flex items-center gap-2 flex-shrink-0">
              <span
                className={
                  ["flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full", badge.className].asClass
                }
                style={badge.style}
              >
                {badge.icon}
                {badge.label}
              </span>
              {!talent.preview && (
                <button
                  className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full text-white hover:opacity-90 transition-opacity"
                  style={{backgroundColor: "#F07340"}}
                  onClick={(e) => {
                    e.stopPropagation();
                    goTo?.();
                  }}
                >
                  View Profile
                </button>
              )}
            </div>
          </div>

          {/* Bio with read more/less */}
          {bio && (
            <p className="text-xs text-brand-muted mt-2 leading-relaxed">
              {bioExpanded
                ? bio
                : bio.slice(0, ACCOUNTANT_BIO_TRUNCATE_LENGTH) +
                  (bio.length > ACCOUNTANT_BIO_TRUNCATE_LENGTH ? "…" : "")}
              {bio.length > ACCOUNTANT_BIO_TRUNCATE_LENGTH && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setBioExpanded((v) => !v);
                  }}
                  className="ml-1 text-xs font-medium underline underline-offset-2 hover:opacity-70 transition-opacity"
                  style={{color: "#3D52AD"}}
                >
                  {bioExpanded ? "Show less" : "Read more"}
                </button>
              )}
            </p>
          )}

          {/* Rank (when no bio) */}
          {!bio && <p className="text-xs text-brand-muted mt-1">{talent.rank}</p>}
        </div>
      </div>

      {/* Footer — cert badge tiles */}
      {!!talent.certifications?.length && (
        <div
          className="px-5 py-3 border-t flex flex-wrap items-center gap-2"
          style={{borderColor: "#E8E4DF", backgroundColor: "#FAFAF9"}}
        >
          {talent.certifications.map((name) => {
            const obj = talent.certificationObjects?.find((c) => c.name === name);
            const imgSrc = obj?.imageUrl || certificationImages[name];
            return imgSrc ? (
              <img
                key={name}
                src={imgSrc}
                alt={name}
                title={name}
                className="w-9 h-9 rounded-xl object-contain flex-shrink-0 hover:scale-110 transition-transform cursor-default"
              />
            ) : (
              <div
                key={name}
                title={name}
                className="w-9 h-9 rounded-xl flex items-center justify-center text-xs font-bold flex-shrink-0 hover:scale-110 transition-transform cursor-default"
                style={{backgroundColor: colorForName(name), color: "white"}}
              >
                {shortLabel(name)}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Talent;
